import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate } from "react-router-dom";
import {
  Coins,
  TrendingUp,
  ArrowUpRight,
  ArrowRightLeft,
  PiggyBank,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { companyApi } from "@/services/api";
import { RedeemApplicationModal } from "@/components/company/RedeemApplicationModal";
import { WithdrawModal } from "@/components/company/WithdrawModal";
import { YieldHistoryTable } from "@/components/company/YieldHistoryTable";

// Componente para exibir o rendimento com animação em tempo real
// A animação mostra uma projeção do rendimento do dia atual (D0) que ainda não foi
// confirmado pelo backend. O backend só confirma rendimentos até D-1.
function AnimatedYield({
  yieldAmount,
  principalAmount,
  isActive,
  dailyRate,
  secondsUntilNextBusinessDay,
}: {
  yieldAmount: number;
  principalAmount: number;
  isActive: boolean;
  dailyRate: number;
  secondsUntilNextBusinessDay: number;
}) {
  const rateRef = useRef(dailyRate);
  const secondsRef = useRef(secondsUntilNextBusinessDay);
  const initialSecondsRef = useRef(secondsUntilNextBusinessDay);

  useEffect(() => {
    rateRef.current = dailyRate;
    secondsRef.current = secondsUntilNextBusinessDay;
    initialSecondsRef.current = secondsUntilNextBusinessDay;
  }, [dailyRate, secondsUntilNextBusinessDay]);

  // Sempre inicia com o yieldAmount do backend (D-1) como base
  const [displayValue, setDisplayValue] = useState(yieldAmount);

  const animationRef = useRef<number | null>(null);
  const lastUpdateRef = useRef(Date.now());
  const principalRef = useRef(principalAmount);
  const displayValueRef = useRef(yieldAmount);
  const baseYieldRef = useRef(yieldAmount);

  useEffect(() => {
    principalRef.current = principalAmount;
  }, [principalAmount]);

  // Sincroniza com o backend sempre que yieldAmount mudar
  useEffect(() => {
    baseYieldRef.current = yieldAmount;
    displayValueRef.current = yieldAmount;
    setDisplayValue(yieldAmount);
  }, [yieldAmount]);

  useEffect(() => {
    if (!isActive || principalRef.current === 0) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const animate = () => {
      const now = Date.now();
      const elapsed = (now - lastUpdateRef.current) / 1000;
      lastUpdateRef.current = now;

      // Decrementa o tempo até o próximo dia útil
      secondsRef.current = Math.max(secondsRef.current - elapsed, 1);

      // Calcula quanto do dia já passou (proporção)
      const elapsedToday = initialSecondsRef.current - secondsRef.current;
      const proportionOfDay = Math.min(elapsedToday / initialSecondsRef.current, 1);

      // Rendimento projetado do dia = (principal + yield acumulado) × taxa diária × proporção do dia
      // Juros compostos: o rendimento do dia incide sobre o saldo atualizado
      const projectedDailyYield = (principalRef.current + baseYieldRef.current) * rateRef.current * proportionOfDay;

      // Valor exibido = rendimento confirmado (D-1) + projeção do dia atual
      displayValueRef.current = baseYieldRef.current + projectedDailyYield;
      setDisplayValue(displayValueRef.current);

      animationRef.current = requestAnimationFrame(animate);
    };

    lastUpdateRef.current = Date.now();
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  // Formatar com 4 casas decimais no padrão brasileiro
  const formattedValue = displayValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

  return (
    <span className="font-mono tabular-nums">R$ {formattedValue}</span>
  );
}

interface DashboardData {
  balance: {
    brld: string;
    brldWei: string;
    brl: number;
    yield: number;
    availableBalance: number;
    appliedBalance: number;
    usdcBalance: number;
  };
  position: {
    totalPrincipal: number;
    totalCurrent: number;
    yieldAmount: number;
    accumulatedFactor: number;
    businessDays: number;
  };
  product: {
    cdiRate: number;
    annualRate: number;
  };
  wallet: {
    address: string;
    networkId: string;
    networkName: string;
    explorerUrl: string;
  };
  animation: {
    nextBusinessDayAt: string;
    currentDailyRate: number;
    secondsUntilNextBusinessDay: number;
  };
}

export function CompanyBrldPage() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const data = await companyApi.dashboard.get();
      setDashboardData(data as DashboardData);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);


  const balance = dashboardData?.balance || {
    brld: "0",
    brl: 0,
    yield: 0,
    availableBalance: 0,
    appliedBalance: 0,
    usdcBalance: 0,
  };
  const position = dashboardData?.position || {
    totalPrincipal: 0,
    totalCurrent: 0,
    yieldAmount: 0,
    accumulatedFactor: 1,
    businessDays: 0,
  };
  const product = dashboardData?.product || { cdiRate: 95, annualRate: 12.59 };
  const animation = dashboardData?.animation || {
    nextBusinessDayAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    currentDailyRate: 0.00043 * 0.95,
    secondsUntilNextBusinessDay: 24 * 60 * 60,
  };

  // Calcular valores
  const availableBalanceReais = balance.availableBalance / 100;
  const appliedBalanceReais = balance.appliedBalance / 100;
  const yieldAmount = position.yieldAmount || 0;
  const totalBrld = availableBalanceReais + appliedBalanceReais + yieldAmount;
  const principalAmount = position.totalPrincipal || appliedBalanceReais;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Coins className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="BRLD"
        description="Seu saldo em Real Digital"
        stats={[
          {
            value: loading ? "..." : formatCurrency(totalBrld),
            label: "Saldo Total BRLD",
          },
          {
            value: loading ? "..." : `${product.annualRate.toFixed(2)}%`,
            label: "Taxa a.a.",
          },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              onClick={loadDashboard}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        }
      />

      {/* Cards lado a lado: Disponível e Aplicado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card Disponível */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Coins className="w-5 h-5 text-muted-foreground" />
                Disponível
              </CardTitle>
              <Badge variant="outline" className="text-muted-foreground">
                Não rende
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div>
                  <p className="text-3xl font-bold">{formatCurrency(availableBalanceReais)}</p>
                </div>

                {availableBalanceReais > 0 && (
                  <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border">
                    <AlertCircle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      Este saldo não está rendendo. Aplique para ganhar {product.cdiRate}% do CDI.
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full"
                    onClick={() => navigate("/aplicar")}
                    disabled={availableBalanceReais <= 0}
                  >
                    <PiggyBank className="w-4 h-4 mr-2" />
                    Aplicar
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => navigate("/converter")}
                      disabled={availableBalanceReais <= 0}
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Converter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowWithdrawModal(true)}
                      disabled={availableBalanceReais <= 0}
                    >
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      Resgatar
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card Aplicado */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-muted-foreground" />
                Aplicado
              </CardTitle>
              <Badge variant="outline" className="text-green-600 border-green-500/50">
                Rende {product.cdiRate}% CDI
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div>
                  <p className="text-3xl font-bold">{formatCurrency(appliedBalanceReais)}</p>
                  {yieldAmount > 0 && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      + {yieldAmount.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 4, maximumFractionDigits: 4 })} rendimento
                    </p>
                  )}
                  {(appliedBalanceReais > 0 || yieldAmount > 0) && (
                    <p className="text-lg font-semibold text-muted-foreground mt-2">
                      Total: {formatCurrency(appliedBalanceReais + yieldAmount)}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Taxa</p>
                    <p className="text-sm font-semibold">
                      {product.cdiRate}% CDI ({product.annualRate.toFixed(2)}% a.a.)
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Dias</p>
                    <p className="text-sm font-semibold">
                      {position.businessDays} dias úteis
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowRedeemModal(true)}
                  disabled={appliedBalanceReais + yieldAmount <= 0}
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Resgatar Aplicação
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rendimento em destaque */}
      {(yieldAmount > 0 || appliedBalanceReais > 0) && (
        <Card className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Rendimento Acumulado</p>
                <p className="text-3xl font-bold mt-1">
                  <AnimatedYield
                    yieldAmount={yieldAmount}
                    principalAmount={principalAmount}
                    isActive={appliedBalanceReais > 0}
                    dailyRate={animation.currentDailyRate}
                    secondsUntilNextBusinessDay={animation.secondsUntilNextBusinessDay}
                  />
                </p>
                <p className="text-green-100 text-sm mt-2">
                  Rendendo {product.cdiRate}% do CDI diariamente
                </p>
              </div>
              <TrendingUp className="w-16 h-16 text-white/20" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Rendimentos */}
      <YieldHistoryTable />

      {/* Info sobre funcionamento */}
      <Card>
        <CardHeader>
          <CardTitle>Como funciona o saldo BRLD</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-foreground">Saldo Disponível</p>
              <p>
                Quando você deposita via PIX, seu saldo entra como "Disponível". Ele pode ser usado
                para converter em USDC, resgatar para conta bancária ou aplicar para render.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-foreground">Saldo Aplicado</p>
              <p>
                Ao aplicar, seu saldo passa a render {product.cdiRate}% do CDI automaticamente.
                O rendimento é calculado diariamente em dias úteis (D+1).
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium text-foreground">Resgate da Aplicação</p>
              <p>
                A qualquer momento você pode resgatar a aplicação. O valor (principal + rendimento)
                volta para o saldo Disponível, de onde você pode converter ou resgatar para conta.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modais */}
      <RedeemApplicationModal
        open={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
        appliedBalance={appliedBalanceReais}
        yieldAmount={yieldAmount}
        onSuccess={loadDashboard}
      />

      <WithdrawModal
        open={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={availableBalanceReais}
        onSuccess={loadDashboard}
      />
    </div>
  );
}
